import { FileInputIcon, PlusIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useNovelCoreStore } from '@/stores/novelCoreStore'
import { useMemo } from 'react'
import { useRouter } from '@/router/hooks'
import { exportThesisMarkdown } from '@/lib/exportThesisMarkdown'
import ThesisCloudRestore from '@/components/ThesisCloudRestore'

const NovelsIndex = () => {
  const { novels } = useNovelCoreStore()
  const router = useRouter()

  const sorted = useMemo(
    () =>
      [...novels].sort(
        (a, b) =>
          new Date(b.meta.updatedAt).getTime() - new Date(a.meta.updatedAt).getTime()
      ),
    [novels]
  )

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('zh-CN', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return iso
    }
  }

  const methodLabel = (labels?: { id: string; label: string }[]) =>
    labels?.find((l) => l.id === 'method')?.label?.replace(/^方法：/, '') || 'MBA 论文'

  const researchQ = (labels?: { id: string; label: string }[]) =>
    labels?.find((l) => l.id === 'rq')?.label?.replace(/^研究问题：/, '') || ''

  return (
    <>
      <section className="thesis-hero">
        <div>
          <p className="thesis-hero__eyebrow">MBA 论文</p>
          <h1 className="thesis-hero__title">把研究问题写清楚，再一章章写下去</h1>
          <p className="thesis-hero__lead">
            论文台不是一键代写。先收窄问题，再搭六章目录，分章写正文、做编审。注册即送额度；提示词内置，生成前可补一句偏好。
          </p>
          <div className="thesis-hero__steps">
            <span className="thesis-chip">1 题目与问题</span>
            <span className="thesis-chip">2 六章目录</span>
            <span className="thesis-chip">3 分章正文</span>
            <span className="thesis-chip">4 编审</span>
          </div>
        </div>
        <aside className="thesis-hero__panel">
          <h3>本季建议</h3>
          <ol>
            <li>研究问题收窄到一学期能回答</li>
            <li>方法章写清样本与证据来源</li>
            <li>分析章按问题组织，避免流水账</li>
            <li>结论必须回扣开题问题</li>
          </ol>
        </aside>
      </section>

      <div className="thesis-toolbar">
        <div>
          <p className="thesis-toolbar__title">论文库</p>
          <p className="thesis-toolbar__meta">{sorted.length} 篇 · 点击进入正文</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/papers/create" className="thesis-btn thesis-btn--primary">
            <PlusIcon size={18} />
            新建论文
          </Link>
          <button
            type="button"
            className="thesis-btn thesis-btn--ghost"
            onClick={() => exportThesisMarkdown()}
          >
            <FileInputIcon size={16} />
            导出
          </button>
        </div>
      </div>

      <ThesisCloudRestore />

      {sorted.length === 0 ? (
        <div className="thesis-empty">
          <p>本机还没有论文。若换过设备，请先在上方「云端论文」下载到本机；或点右上角「新建论文」开题。</p>
        </div>
      ) : (
        <div className="thesis-grid">
          {sorted.map((novel) => {
            const rq = researchQ(novel.attributes.labels)
            return (
              <button
                key={novel.id}
                type="button"
                className="thesis-card"
                onClick={() => router.push(`/papers/${novel.id}/manuscript`)}
              >
                <span className="thesis-card__tag">{methodLabel(novel.attributes.labels)}</span>
                <h2 className="thesis-card__title">{novel.attributes.title}</h2>
                {rq ? <p className="text-sm text-[var(--tt-muted)] line-clamp-2">{rq}</p> : null}
                <span className="thesis-card__meta">{formatTime(novel.meta.updatedAt)}</span>
              </button>
            )
          })}
        </div>
      )}
    </>
  )
}

export default NovelsIndex
